import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line, LabelList } from "recharts";
import { fmtNumber, fmtRupiah } from "@/lib/api";
import { Timer, Truck } from "lucide-react";

const TooltipStyle = { background: "#0B132B", border: "1px solid #334155", borderRadius: 10, color: "#F8FAFC", fontSize: 12 };
const shortRp = (v) => (v >= 1e9 ? `${(v / 1e9).toFixed(2)} M` : v >= 1e6 ? `${Math.round(v / 1e6)} jt` : `${Math.round((v || 0) / 1e3)} rb`);

export { DeptBar } from "@/components/DeptBar";

const SupplierLabel = ({ x, y, width, height, index, data }) => {
  const d = data[index];
  if (!d) return null;
  return (
    <text x={x + width + 8} y={y + height / 2} dy={4} fontSize={11} fill="#E2E8F0" fontWeight={600}>
      <tspan>{`${fmtNumber(d.value)} item`}</tspan>
      <tspan fill="#94A3B8">{` (${d.pct}%) `}</tspan>
      <tspan fill="#6EE7B7">{shortRp(d.amount)}</tspan>
    </text>
  );
};

export const TopSuppliers = ({ data }) => (
  <div data-testid="top-suppliers-card" className="rounded-xl border border-slate-700/60 bg-[#111D38]/70 backdrop-blur-md p-5 h-full">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-slate-100 font-semibold text-base">Top Suppliers</h3>
      <span className="text-[10px] uppercase tracking-widest text-slate-500">ITEM · SHARE · AMOUNT</span>
    </div>
    <div className="overflow-x-auto"><div className="h-80 min-w-[560px]">
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 150, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" horizontal={false} />
          <XAxis type="number" stroke="#94A3B8" fontSize={11} />
          <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={140} tickFormatter={(v) => (v || "").length > 20 ? v.slice(0, 20) + "…" : v} />
          <Tooltip contentStyle={TooltipStyle} cursor={{ fill: "rgba(59,130,246,0.08)" }}
            formatter={(v, n, p) => [`${fmtNumber(v)} item (${p.payload.pct}%) · ${fmtRupiah(p.payload.amount)}`, "Supplier"]} />
          <Bar dataKey="value" fill="#38BDF8" radius={[0, 8, 8, 0]}>
            <LabelList dataKey="value" content={(props) => <SupplierLabel {...props} data={data} />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div></div>
  </div>
);

const LeadStat = ({ icon: Icon, label, days, count, color, testId }) => (
  <div data-testid={testId} className="flex-1 rounded-lg border border-slate-700/60 bg-[#0B132B]/60 px-4 py-3 flex items-center gap-3">
    <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-white/5" style={{ color }}><Icon className="h-4 w-4" /></div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-2xl font-extrabold text-slate-50">{days} <span className="text-sm font-medium text-slate-400">hari</span></div>
      <div className="text-[10px] text-slate-500">rata-rata dari {fmtNumber(count)} item</div>
    </div>
  </div>
);

export const LeadTimeProcess = ({ data }) => (
  <div data-testid="lead-time-card" className="rounded-xl border border-slate-700/60 bg-[#111D38]/70 backdrop-blur-md p-5 h-full">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-slate-100 font-semibold text-base">Lead Time Proses & Supply</h3>
      <span className="text-[10px] uppercase tracking-widest text-slate-500">AVG DAYS</span>
    </div>
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <LeadStat testId="lead-pr-po" icon={Timer} label="PR to PO" days={data?.pr_po ?? 0} count={data?.pr_po_count ?? 0} color="#8B5CF6" />
      <LeadStat testId="lead-po-gr" icon={Truck} label="PO Share to GR" days={data?.po_gr ?? 0} count={data?.po_gr_count ?? 0} color="#F59E0B" />
    </div>
    <div className="h-52">
      <ResponsiveContainer>
        <LineChart data={data?.trend || []} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1F2A44" />
          <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
          <YAxis stroke="#94A3B8" fontSize={11} width={36} />
          <Tooltip contentStyle={TooltipStyle} formatter={(v, n) => [`${v} hari`, n]} />
          <Legend wrapperStyle={{ color: "#CBD5E1", fontSize: 11 }} />
          <Line type="monotone" dataKey="pr_po" name="PR to PO" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3, fill: "#8B5CF6" }} />
          <Line type="monotone" dataKey="po_gr" name="PO Share to GR" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: "#F59E0B" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);
